import type { TOC } from '@ember/component/template-only';
import DonutChart from './charts/donut-chart.gts';
import PieChart from './charts/pie-chart.gts';
import BarChart from './charts/bar-chart.gts';
import HorizontalBarChart from './charts/horizontal-bar-chart.gts';
import LineChart from './charts/line-chart.gts';
import AreaChart from './charts/area-chart.gts';
import SummaryDisplay from './charts/summary-display.gts';
import type { ChartDataPoint, ChartType, ChartOptions, AxisOptions } from '../types/charts.ts';

// Chart – unified wrapper that delegates to the correct chart component
// based on the @type argument. Drop-in replacement for the Vega setupChart
// modifier pattern.

interface ChartSignature {
  Args: ChartOptions &
    AxisOptions & {
      /** Chart data points */
      data: ChartDataPoint[];
      /** Which chart type to render */
      type: ChartType;
      /**
       * Height in px. For cartesian charts it's the fixed canvas height
       * (default: 300). For pie/donut a bounded value opts into scale-to-fit
       * (the chart shrinks to the box instead of clipping); omit for the
       * legacy container-driven sizing.
       */
      height?: number;
      /** Ring thickness for donut charts (default: 60) */
      ringThickness?: number;
      /** Show point markers for line charts (default: true) */
      showPoints?: boolean;
      /** Fill opacity for area charts (default: 0.3) */
      fillOpacity?: number;
      /** Label for summary display */
      label?: string;
      /** Compact mode for summary display in dashboard tiles */
      compact?: boolean;
      /** Whether to sort horizontal bars (default: true) */
      sorted?: boolean;
    };
  Element: HTMLDivElement;
}

function isEqual(a: unknown, b: unknown) {
  return a === b;
}

const Chart: TOC<ChartSignature> = <template>
  {{#if (isEqual @type "donut")}}
    <DonutChart
      @data={{@data}}
      @colors={{@colors}}
      @onClick={{@onClick}}
      @selectedPoint={{@selectedPoint}}
      @showLegend={{@showLegend}}
      @legendPosition={{@legendPosition}}
      @ringThickness={{@ringThickness}}
      @animate={{@animate}}
      @formatValue={{@formatValue}}
      @height={{@height}}
      ...attributes
    />
  {{else if (isEqual @type "pie")}}
    <PieChart
      @data={{@data}}
      @colors={{@colors}}
      @onClick={{@onClick}}
      @selectedPoint={{@selectedPoint}}
      @showLegend={{@showLegend}}
      @legendPosition={{@legendPosition}}
      @animate={{@animate}}
      @formatValue={{@formatValue}}
      @height={{@height}}
      ...attributes
    />
  {{else if (isEqual @type "bar")}}
    <BarChart
      @data={{@data}}
      @colors={{@colors}}
      @onClick={{@onClick}}
      @height={{@height}}
      @showXAxis={{@showXAxis}}
      @showYAxis={{@showYAxis}}
      @xLabel={{@xLabel}}
      @yLabel={{@yLabel}}
      @formatValue={{@formatValue}}
      @minimumTickStep={{@minimumTickStep}}
      ...attributes
    />
  {{else if (isEqual @type "horizontalBar")}}
    <HorizontalBarChart
      @data={{@data}}
      @colors={{@colors}}
      @onClick={{@onClick}}
      @sorted={{@sorted}}
      @showXAxis={{@showXAxis}}
      @showYAxis={{@showYAxis}}
      @xLabel={{@xLabel}}
      @yLabel={{@yLabel}}
      @formatValue={{@formatValue}}
      @minimumTickStep={{@minimumTickStep}}
      ...attributes
    />
  {{else if (isEqual @type "line")}}
    <LineChart
      @data={{@data}}
      @colors={{@colors}}
      @onClick={{@onClick}}
      @height={{@height}}
      @showPoints={{@showPoints}}
      @showXAxis={{@showXAxis}}
      @showYAxis={{@showYAxis}}
      @xLabel={{@xLabel}}
      @yLabel={{@yLabel}}
      @formatValue={{@formatValue}}
      @minimumTickStep={{@minimumTickStep}}
      ...attributes
    />
  {{else if (isEqual @type "area")}}
    <AreaChart
      @data={{@data}}
      @colors={{@colors}}
      @onClick={{@onClick}}
      @height={{@height}}
      @fillOpacity={{@fillOpacity}}
      @showXAxis={{@showXAxis}}
      @showYAxis={{@showYAxis}}
      @xLabel={{@xLabel}}
      @yLabel={{@yLabel}}
      @formatValue={{@formatValue}}
      @minimumTickStep={{@minimumTickStep}}
      ...attributes
    />
  {{else if (isEqual @type "summary")}}
    <SummaryDisplay @data={{@data}} @label={{@label}} @compact={{@compact}} ...attributes />
  {{/if}}
</template>;

export default Chart;

export type { ChartSignature };
